import logger from "../utils/logger";
// src/components/GlobalErrorBoundary.jsx
import React, { Component } from "react";
import { isChunkLoadError, triggerStaleChunkReload } from "../utils/lazyWithRetry";

export class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      timestamp: null,
      pathname: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error) {
    const isChunk = isChunkLoadError(error);
    return {
      hasError: true,
      error,
      isChunkError: isChunk,
      timestamp: new Date().toISOString(),
      pathname: typeof window !== "undefined" ? window.location.pathname + window.location.search : "unknown",
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      logger.error("[BLANK_PAGE_DIAGNOSTIC][ERROR][BOUNDARY_CAUGHT]", {
        message: error?.message || String(error),
        stack: error?.stack || null,
        componentStack: errorInfo?.componentStack || null,
        pathname: typeof window !== "undefined" ? window.location.pathname + window.location.search : "unknown",
        timestamp: new Date().toISOString(),
        isChunkError: isChunkLoadError(error),
      });
    } catch (_) {}

    // If an unhandled chunk loading error reaches the root boundary, trigger controlled recovery
    if (isChunkLoadError(error)) {
      triggerStaleChunkReload(error);
    }
  }

  componentDidMount() {
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", this.handlePopState);
    }
  }

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("popstate", this.handlePopState);
    }
  }

  handlePopState = () => {
    if (this.state.hasError) {
      this.handleReset();
    }
  };

  handleReload = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  handleReset = () => {
    // If a chunk load error failed, attempting a soft React reset will fail again due to cached rejected import
    if (this.state.isChunkError) {
      this.handleReload();
      return;
    }
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      timestamp: null,
      pathname: null,
      isChunkError: false,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-6 font-sans antialiased text-slate-800">
          <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-slate-200/80 p-8 space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 font-bold text-xl shrink-0">
                !
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                  {this.state.isChunkError ? "Application Update Detected" : "Application Error Caught"}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {this.state.isChunkError
                    ? "A newer version of the application was deployed. Please reload the page to load updated assets."
                    : "An unexpected render exception was prevented from blanking the screen."}
                </p>
              </div>
            </div>

            <div className="bg-slate-900 rounded-xl p-4 text-xs font-mono text-slate-200 overflow-x-auto space-y-2 max-h-60 overflow-y-auto">
              <div>
                <span className="text-slate-400">Path: </span>
                <span className="text-amber-400">{this.state.pathname}</span>
              </div>
              <div>
                <span className="text-slate-400">Time: </span>
                <span className="text-slate-300">{this.state.timestamp}</span>
              </div>
              <div className="pt-2 border-t border-slate-800 text-rose-300 whitespace-pre-wrap">
                {this.state.error?.message || String(this.state.error)}
              </div>
              {this.state.error?.stack && (
                <div className="text-[11px] text-slate-500 whitespace-pre-wrap pt-1">
                  {this.state.error.stack.split("\n").slice(0, 5).join("\n")}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="flex-1 py-2.5 px-4 rounded-xl bg-[#033F99] text-white font-semibold text-sm hover:bg-blue-800 transition-all active:scale-95 shadow-sm text-center cursor-pointer"
              >
                Reload Page
              </button>
              <button
                type="button"
                onClick={this.handleReset}
                className="py-2.5 px-4 rounded-xl bg-slate-100 text-slate-700 font-semibold text-sm hover:bg-slate-200 transition-all active:scale-95 border border-slate-200 text-center cursor-pointer"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
